/**
 * Support Request Panel Component
 * Allows users to submit support requests/tickets
 */

import React, { useState } from 'react';
import { Send, Loader2, HelpCircle, X, CheckCircle } from 'lucide-react';
import { userApi } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface SupportRequestPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const SUPPORT_CATEGORIES = [
  { value: 'bug_report', label: 'Bug Report' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'account_issue', label: 'Account Issue' },
  { value: 'billing', label: 'Billing Question' },
  { value: 'technical_support', label: 'Technical Support' },
  { value: 'general_inquiry', label: 'General Inquiry' },
  { value: 'other', label: 'Other' },
];

export const SupportRequestPanel: React.FC<SupportRequestPanelProps> = ({
  isOpen,
  onClose,
}) => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    category: '',
  });
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
    ticketId?: string;
  }>({ type: null, message: '' });

  const resetForm = () => {
    setFormData({
      subject: '',
      message: '',
      category: '',
    });
    setSubmitStatus({ type: null, message: '' });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    // Validate form
    if (!formData.subject.trim()) {
      setSubmitStatus({ type: 'error', message: 'Please enter a subject' });
      return;
    }
    if (!formData.category) {
      setSubmitStatus({ type: 'error', message: 'Please select a category' });
      return;
    }
    if (!formData.message.trim()) {
      setSubmitStatus({ type: 'error', message: 'Please enter a message' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      const response = await userApi.submitSupportRequest(
        formData.subject,
        formData.message,
        formData.category
      );

      setSubmitStatus({
        type: 'success',
        message: 'Your support request has been submitted successfully. Our team will respond to your email shortly.',
        ticketId: response.ticket_id,
      });

      // Reset form after successful submission
      setFormData({
        subject: '',
        message: '',
        category: '',
      });
    } catch (error) {
      console.error('Failed to submit support request:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Failed to submit support request. Please try again or email feedback@patentloop.com directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5" />
            Submit Support Request
          </DialogTitle>
          <DialogDescription>
            Submit a support request and our team will get back to you via email.
          </DialogDescription>
        </DialogHeader>

        {submitStatus.type === 'success' ? (
          <div className="py-6">
            <div className="flex flex-col items-center text-center gap-4">
              <div className="rounded-full bg-green-100 p-3">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-green-600">Request Submitted</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  {submitStatus.message}
                </p>
                {submitStatus.ticketId && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Ticket ID: <span className="font-mono">{submitStatus.ticketId}</span>
                  </p>
                )}
              </div>
              <Button onClick={handleClose} className="mt-4">
                Close
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-4">
              {/* User info display */}
              <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
                <p>Submitting as: <span className="font-medium">{user?.username || 'Unknown'}</span></p>
                <p>Email: <span className="font-medium">{user?.email || 'Not available'}</span></p>
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, category: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPORT_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  id="subject"
                  placeholder="Brief description of your issue"
                  value={formData.subject}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, subject: e.target.value }))
                  }
                  maxLength={200}
                />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  placeholder="Please describe your issue or request in detail..."
                  value={formData.message}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, message: e.target.value }))
                  }
                  rows={6}
                  className="resize-none"
                />
              </div>

              {/* Error message */}
              {submitStatus.type === 'error' && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                  {submitStatus.message}
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Submit Request
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SupportRequestPanel;
